// Map picker shown after HomeScreen. Card layout (thumbnail / tilt / stage
// position) is sourced per-bundle from `map.json`'s pickerCard block, so
// adding a new map is pure data + a pickerCard entry.
//
// Cards animate in place into a larger "selected" card (position, size,
// border-radius, rotation, label size all animating via CSS transitions),
// reveal a sticky-note hint beside them, and drain a progress bar while a
// caller-supplied preload promise runs in parallel. Once both the bar and
// the preload resolve, the screen wipes out via clip-path to reveal the
// canvas underneath.
//
// Any bundle with `hiddenFromPicker: true` is excluded from this UI but
// still reachable via debug.transport for development.

import { listBundles, type MapBundle } from '../maps/registry';
import { menuAudio } from './MenuAudio';
import { MenuNav, type MenuItem } from './MenuNav';
import { t } from '../i18n';

const STAGE_W = 1920;
const STAGE_H = 1080;

const LOADING_DURATION_MS = 3750;
const MORPH_DURATION_MS   = 450;
// Hover / focus "float up" duration. Uses its own (fast) easing via a
// separate `translate` property so it doesn't drag on the 450ms morph
// clock that covers position, size, and rotation.
const FLOAT_DURATION_MS   = 150;
const WIPE_DURATION_MS    = 700;

// Home-music lowpass transition. Starts the moment the user commits to a
// map and sweeps to `TARGET_HZ` over `DURATION_MS`, so the track is clearly
// muffled by the time the wipe-phase volume fade takes it to silence — the
// "closing distance" effect as the game approaches. Target is a classic
// behind-the-door cutoff; duration fills most of the loading window so the
// sweep reads as gradual rather than a snap.
const MUSIC_SWEEP_TARGET_HZ = 500;
const MUSIC_SWEEP_DURATION_MS = 1400;

// Ordered list of visible maps. Sorted by pickerCard.left so DOM + MenuNav
// col index agree with visual left-to-right order — otherwise the controller
// "right" key moves to whichever map happens to be next in registry-iteration
// order (alphabetical by bundle dir), which is unrelated to on-screen layout.
function visibleBundles(): MapBundle[] {
  return listBundles()
    .filter((b) => !b.json.hiddenFromPicker && b.json.pickerCard)
    .sort((a, b) => (a.json.pickerCard!.left - b.json.pickerCard!.left));
}

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

#map-select {
  position: fixed; inset: 0; z-index: 99;
  background: url('/ui/map-selection-bg.webp') center center / cover no-repeat #1a1a1a;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  color: #FFFFFF;
  overflow: hidden;
  clip-path: inset(0 0 0 0);
  transition: clip-path ${WIPE_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1);
}
#map-select.wiping-out { clip-path: inset(0 100% 0 0); pointer-events: none; }

#map-select .stage {
  position: absolute; top: 50%; left: 50%;
  width: ${STAGE_W}px; height: ${STAGE_H}px;
  transform-origin: center center;
  transform: translate(-50%, -50%) scale(1);
}

#map-select .title {
  position: absolute; left: 0; right: 0; top: 80px;
  text-align: center;
  font-family: 'Chewy', sans-serif;
  font-weight: 400; font-size: 48px; line-height: 1;
  color: #FFFFFF;
  opacity: 1; transition: opacity 220ms ease;
}
#map-select.selected .title { opacity: 0; }

/* Card — applies to both picking-state and selected-state. The .selected
   modifier overrides position/size/rotation/radius via longer-running
   CSS transitions to create the morph. Dismissed siblings fade + shrink. */
#map-select .card {
  --rot: 0deg;
  --base-left: 0px;
  --base-top: 0px;
  position: absolute;
  left: var(--base-left);
  top: var(--base-top);
  width: 400px;
  padding: 12px;
  box-sizing: border-box;
  background: #F6F7F8;
  border-radius: 22px;
  box-shadow: 0px 10px 16px rgba(0,0,0,0.48), inset 0px -4px 2px rgba(0,0,0,0.15);
  cursor: pointer;
  border: 0;
  text-align: center;
  color: inherit;
  font-family: inherit;
  display: block;
  transform-origin: center center;
  transform: rotate(var(--rot));
  /* Using the individual translate property (not baked into transform) so
     the hover/focus float can ride its own fast transition timeline while
     the morph's position/size/rotation stay on MORPH_DURATION_MS. */
  translate: 0 0;
  outline: 0 solid rgba(255, 255, 255, 0);
  outline-offset: 3px;
  transition:
    left ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    top ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    width ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    padding ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    border-radius ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    box-shadow ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    transform ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    translate ${FLOAT_DURATION_MS}ms cubic-bezier(0.2, 0.9, 0.2, 1),
    outline-color ${FLOAT_DURATION_MS}ms ease,
    outline-width ${FLOAT_DURATION_MS}ms ease,
    opacity 200ms ease;
}
#map-select .card:hover,
#map-select .card.focused {
  translate: 0 -4px;
  /* Same focus ring as the changing room — 50% white so it reads over
     any map thumbnail without competing with the card's own shadow. */
  outline: 4px solid rgba(255, 255, 255, 0.5);
}
#map-select.selected .card { cursor: default; pointer-events: none; }
#map-select.selected .card:hover,
#map-select.selected .card.focused {
  translate: 0 0;
  outline: 0 solid rgba(255, 255, 255, 0);
}

#map-select .card .thumb {
  width: 100%;
  height: 210px;
  border-radius: 20px;
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  display: block;
  transition:
    height ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    border-radius ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1);
}

#map-select .card .label {
  font-family: 'Chewy', sans-serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 1;
  color: #59A0DE;
  padding: 20px 0 20px 0;
  margin: 0;
  transition:
    font-size ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1),
    padding ${MORPH_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1);
}

/* Selected big-card state. All overridden via CSS so the morph animates
   purely through transitions on the same DOM node. */
#map-select .card.selected {
  left: 400px;
  top: 220px;
  width: 800px;
  padding: 30px;
  border-radius: 80px;
  box-shadow: 0px 28px 34px rgba(0,0,0,0.55), inset 0px -8px 9px rgba(0,0,0,0.15);
  transform: rotate(-1.96deg);
  z-index: 2;
}
#map-select .card.selected .thumb {
  height: 400px;
  border-radius: 60px;
}
#map-select .card.selected .label {
  font-size: 64px;
  padding: 30px 0 30px 0;
}

#map-select .card.dismissing {
  opacity: 0;
  transform: rotate(var(--rot)) translateY(40px) scale(0.9);
  pointer-events: none;
}

/* Sticky note — positioned behind the big card, peeks out to the right.
   Starts further right + transparent; slides in + fades in on .shown. */
#map-select .sticky {
  position: absolute;
  left: 1160px;
  top: 290px;
  width: 400px;
  height: 450px;
  background:
    linear-gradient(180deg, rgba(250, 156, 34, 0.2) 0%, rgba(250, 156, 34, 0.2) 12.5%, rgba(250, 156, 34, 0) 15.38%),
    #FBE5AB;
  box-shadow: 0px 15px 17px rgba(0,0,0,0.4), inset 0px -6px 9px rgba(0,0,0,0.2);
  border-radius: 30px;
  transform: rotate(2.01deg) translateX(60px);
  opacity: 0;
  pointer-events: none;
  z-index: 1;
  box-sizing: border-box;
  padding: 100px 100px 0 100px;
  font-family: 'Chewy', sans-serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 50px;
  color: #837457;
  transition:
    transform 400ms cubic-bezier(0.3, 0, 0.2, 1) 100ms,
    opacity 400ms ease 100ms;
}
#map-select .sticky.shown {
  transform: rotate(2.01deg) translateX(0);
  opacity: 1;
}

/* Progress bar — bottom center, grows from 0 → 100% over LOADING_DURATION_MS. */
#map-select .bar {
  position: absolute;
  left: 50%;
  bottom: 80px;
  transform: translateX(-50%);
  width: 640px;
  height: 24px;
  background: #B8B8B8;
  border-radius: 12px;
  overflow: hidden;
  opacity: 0;
  transition: opacity 220ms ease;
  z-index: 2;
}
#map-select.selected .bar { opacity: 1; }
#map-select .bar .fill {
  width: 0%;
  height: 100%;
  background: #FA9C22;
  border-radius: 12px;
  box-shadow: inset 0 2px 0 rgba(255,255,255,0.35), inset 0 -2px 0 rgba(0,0,0,0.15);
  transition: width ${LOADING_DURATION_MS}ms linear;
}
#map-select .bar.filling .fill { width: 100%; }
`;

export class MapSelectScreen {
  private root: HTMLElement | null = null;
  private styleEl: HTMLStyleElement | null = null;
  private disposed = false;
  private resizeHandler = this.updateStageScale.bind(this);
  private nav: MenuNav | null = null;
  private visible: MapBundle[] = [];

  mount(): void {
    if (this.root) return;

    this.styleEl = document.createElement('style');
    this.styleEl.textContent = STYLES;
    document.head.appendChild(this.styleEl);

    this.visible = visibleBundles();

    const root = document.createElement('div');
    root.id = 'map-select';
    root.innerHTML = `
      <div class="stage">
        <div class="title">${t('mapSelect.title')}</div>
        ${this.visible.map((b) => {
          const card = b.json.pickerCard!;
          return `
            <button
              class="card"
              data-map="${b.id}"
              style="--base-left: ${card.left}px; --base-top: ${card.top}px; --rot: ${card.rotationDeg}deg;"
            >
              <div class="thumb" style="background-image: url('${card.thumbnail}');"></div>
              <div class="label">${t(`maps.${b.id}.label`)}</div>
            </button>
          `;
        }).join('')}
        <div class="sticky"></div>
        <div class="bar"><div class="fill"></div></div>
      </div>
    `;
    document.body.appendChild(root);
    this.root = root;

    this.updateStageScale();
    addEventListener('resize', this.resizeHandler);

    // Entering map select: play the in/out sweep. Fires again at the start
    // of wipe-out below (same cue, read by the ear as the screen arriving
    // and the screen leaving).
    menuAudio.playMapScreenSweep();
  }

  async show(
    preload: (id: string) => Promise<void>,
    onBeforeWipe?: (id: string) => void
  ): Promise<string | null> {
    if (!this.root) this.mount();
    const root = this.root!;

    const pickedId = await this.waitForPick(root);
    if (pickedId === null) {
      // Controller B / Esc pressed before any card was selected — wipe
      // out and resolve null so the caller can loop back to the home
      // screen without ever constructing match infrastructure.
      this.nav?.dispose();
      this.nav = null;
      menuAudio.playMapScreenSweep();
      root.classList.add('wiping-out');
      await sleep(WIPE_DURATION_MS);
      this.dispose();
      return null;
    }

    // Begin the home-music lowpass sweep on the commit gesture so the
    // filter is already closing during the card morph. Independent of the
    // UI transitions — MenuAudio schedules the ramp on the audio thread.
    menuAudio.sweepLowpass(MUSIC_SWEEP_TARGET_HZ, MUSIC_SWEEP_DURATION_MS);

    // Kick off the selected-state morph by adding classes. Let the morph
    // breathe for 500ms before starting the preload — the preload path
    // does synchronous WebGPU shader compilation + GLB parsing that stalls
    // the main thread and would jank the morph animation if it fires on
    // the same frame as the card click.
    this.enterSelectedState(root, pickedId);
    await sleep(500);
    await Promise.all([sleep(LOADING_DURATION_MS - 500), preload(pickedId)]);

    // Brief hold at 100% so the full bar is visible.
    await sleep(120);

    // onBeforeWipe fires 100ms before the clip wipe begins — callers use
    // it to start cross-cutting transitions (fading out music, starting
    // the game loop) so the sim is already live for the final moments of
    // loading and throughout the wipe reveal.
    onBeforeWipe?.(pickedId);
    await sleep(100);

    // Wipe out and dispose. Play the in/out sweep at the start of the wipe
    // so it reads as the screen leaving into the level. Nav disposed before
    // the wipe so a trailing keypress can't re-activate a dismissing card.
    this.nav?.dispose();
    this.nav = null;
    menuAudio.playMapScreenSweep();
    root.classList.add('wiping-out');
    await sleep(WIPE_DURATION_MS);
    this.dispose();
    return pickedId;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    removeEventListener('resize', this.resizeHandler);
    this.nav?.dispose();
    this.nav = null;
    this.root?.remove();
    this.styleEl?.remove();
    this.root = null;
    this.styleEl = null;
  }

  /** Build a MenuNav across the visible card buttons (row 0, cols 0..n-1) and
   *  resolve with the picked map id when the user activates any card. Default
   *  focus is the middle card so a cold-arrive controller user can
   *  immediately press A to start the most central option. */
  private waitForPick(root: HTMLElement): Promise<string | null> {
    return new Promise<string | null>((resolveOuter) => {
      const cards = Array.from(root.querySelectorAll('.card')) as HTMLButtonElement[];
      let settled = false;

      // Capture-phase Esc / Backspace listener — keyboard parity with
      // the controller B button. Detached on settle so the listener
      // can't leak across re-mounts of MapSelectScreen.
      const onKeyDown = (e: KeyboardEvent): void => {
        if (settled) return;
        if (e.code !== 'Escape' && e.code !== 'Backspace') return;
        e.preventDefault();
        e.stopPropagation();
        settle(null);
      };
      const settle = (v: string | null): void => {
        if (settled) return;
        settled = true;
        removeEventListener('keydown', onKeyDown, { capture: true } as EventListenerOptions);
        resolveOuter(v);
      };
      addEventListener('keydown', onKeyDown, { capture: true });

      const items: MenuItem[] = cards.map((btn, i) => ({
        el: btn,
        row: 0,
        col: i,
        onActivate: () => settle(btn.dataset.map as string)
      }));
      // Default focus = middle card. clamp in case the picker list ever
      // shrinks below 3 entries.
      const initialIndex = Math.min(Math.floor(items.length / 2), items.length - 1);
      // Controller B = back to home — same null-resolution path as Esc.
      this.nav = new MenuNav({
        items,
        initialIndex,
        onCancel: () => settle(null)
      });
      this.nav.mount();
    });
  }

  private enterSelectedState(root: HTMLElement, picked: string): void {
    root.classList.add('selected');
    const cards = Array.from(root.querySelectorAll('.card')) as HTMLButtonElement[];
    for (const card of cards) {
      if (card.dataset.map === picked) {
        card.classList.add('selected');
      } else {
        card.classList.add('dismissing');
      }
    }

    // Fill the sticky note with the picked map's hint, then reveal it.
    const sticky = root.querySelector('.sticky') as HTMLElement | null;
    const bundle = this.visible.find((b) => b.id === picked);
    if (sticky && bundle?.json.loadingHint) sticky.textContent = t(`maps.${bundle.id}.loadingHint`);
    // Small tick so the .shown class gets its own paint and the transition fires.
    requestAnimationFrame(() => {
      if (sticky) sticky.classList.add('shown');
      const bar = root.querySelector('.bar') as HTMLElement | null;
      if (bar) bar.classList.add('filling');
    });
  }

  private updateStageScale(): void {
    if (!this.root) return;
    const stage = this.root.querySelector('.stage') as HTMLElement | null;
    if (!stage) return;
    const s = Math.min(innerWidth / STAGE_W, innerHeight / STAGE_H);
    stage.style.transform = `translate(-50%, -50%) scale(${s})`;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
